/**
 * The AI Form Replicator's review vocabulary — typed to what
 * `GET /api/v1/forms/extractions/:id` actually returns.
 *
 * CASING
 * ------
 * snake_case, all of it. That endpoint hands back the raw DB row (`SELECT r.*`)
 * plus three columns joined off the source document. It is NOT the camelCase
 * shape `GET /forms/templates` uses, and reading one through the other is the
 * blank-field bug this repo keeps re-finding. See the casing table in
 * `form-library.ts`.
 *
 * WHAT IS DELIBERATELY NOT HERE
 * -----------------------------
 * Sections. There is no `sections` key and no section tree — `fields` is a flat
 * array, and `section_id` was set on 0 of 41 fields on the first real
 * extraction (Pass 2's prompt never asks for one). A "section" is a
 * `section_header` FIELD with ordinary fields following it, which is why
 * `buildOutline` derives the grouping instead of reading one.
 */

import type { FormLibraryTemplate } from './form-library';
import type { PreuseTarget } from './preuse-extraction';

/**
 * The 14 field types this system can render.
 *
 * Mirrors `FIELD_TYPES` in the API's `form-schema.validator.ts` exactly, which
 * in turn was read off the render branches in `DynamicSafetyFormScreen.tsx` and
 * the picker in `SafetyFormBuilderScreen.tsx`. A value here with no render
 * branch there is a field that draws as nothing.
 *
 * KNOWN DUPLICATE: `SafetyFormBuilderScreen.tsx` declares its own copy of this
 * list (with icons) at the top of the file. That file is off-limits in this
 * change, so the two coexist for now. When it is next opened, its local array
 * should be deleted in favour of this one — a second list is exactly how the
 * category select ended up offering values no tenant had (#345).
 */
export const FORM_FIELD_TYPES = [
  'section_header',
  'instructions',
  'text',
  'textarea',
  'number',
  'yes_no',
  'checkbox_group',
  'dropdown',
  'date',
  'time',
  'photo',
  'signature',
  'gps_location',
  'worker_select',
] as const;

export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

/**
 * Exhaustive, never a fallback branch. `Record<FormFieldType, string>` so a new
 * type fails the build here rather than rendering a raw `worker_select` at a
 * reviewer — the label-map rule from CLAUDE.md.
 */
export const FORM_FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  section_header: 'Section Header',
  instructions: 'Instructions',
  text: 'Text Input',
  textarea: 'Multi-line Text',
  number: 'Number',
  yes_no: 'Yes / No',
  checkbox_group: 'Checkbox Group',
  dropdown: 'Dropdown',
  date: 'Date',
  time: 'Time',
  photo: 'Photo',
  signature: 'Signature',
  gps_location: 'GPS Location',
  worker_select: 'Worker Select',
};

const FIELD_TYPE_SET: ReadonlySet<string> = new Set(FORM_FIELD_TYPES);

export function isKnownFieldType(type: unknown): type is FormFieldType {
  return typeof type === 'string' && FIELD_TYPE_SET.has(type);
}

/** Types whose `options.choices` must be present and non-empty. */
export const CHOICE_TYPES: ReadonlySet<string> = new Set(['dropdown', 'checkbox_group']);

/**
 * Types that hold no answer, and so cannot be `is_required`.
 *
 * Not cosmetic and not ours to soften: submission-time validation SKIPS these,
 * so a required signature would be unenforceable and a required section header
 * unsatisfiable — either way the crew gets a form that will not submit. The API
 * rejects it at PATCH and again at approve.
 */
export const NON_ANSWERABLE_TYPES: ReadonlySet<string> = new Set([
  'section_header',
  'instructions',
  'signature',
  'photo',
]);

export function isNonAnswerable(type: string | undefined): boolean {
  return !!type && NON_ANSWERABLE_TYPES.has(type);
}

/**
 * Below this, a proposed field wears the amber treatment. EXCLUSIVE — 0.90
 * itself is not low.
 *
 * Chosen from the observed distribution on two independent runs of the same
 * 2-page JSA, not from a round number. Run 2's 42 fields scored:
 *
 *   0.85 ×1   0.88 ×2   0.90 ×9   0.92 ×12   0.95 ×3   0.97 ×8   0.98 ×1   0.99 ×6
 *
 * `< 0.90` flags exactly three — the risk / severity / probability trio — and
 * those are precisely the three the model itself called out in `ai_notes`
 * ("The 'Risk' field is ambiguous … modelled as 'number' but confidence is
 * slightly lower"). The amber agrees with the prose.
 *
 * The next cut up, `< 0.92`, would amber 12 of 42 including every task-step
 * field the model was content with. A third of the form in amber is decoration,
 * and a reviewer learns to scroll past it.
 */
export const LOW_CONFIDENCE_THRESHOLD = 0.9;

// ─── Payload shapes ───────────────────────────────────────────────────────────

/**
 * One proposed field.
 *
 * Every key below was present on a live extraction before it was written here.
 * `placeholder`, `default_value` and `section_id` are accepted by the API's
 * validator but Pass 2 has never emitted one — they are optional and read
 * defensively rather than declared as though they arrive.
 */
export interface ProposedField {
  id?: string;
  field_type?: string;
  label?: string;
  placeholder?: string | null;
  help_text?: string | null;
  is_required?: boolean;
  sort_order?: number;
  /** Live shape: `{ choices: string[] }`, with an optional sibling `maps_to`. */
  options?: { choices?: unknown; maps_to?: unknown } | null;
  /** Live shape: a SINGLE rule, `{ show_if: { field_id, equals } }` — not an array. */
  conditional_logic?: { show_if?: { field_id?: unknown; equals?: unknown } } | null;
  default_value?: string | null;
  section_id?: string | null;
}

/**
 * `proposed_schema_json`.
 *
 * The index signature is load-bearing, not laziness. PATCH is a FULL
 * REPLACEMENT and the server preserves arbitrary top-level keys verbatim, so a
 * key this interface does not name still has to survive a round trip. Two
 * arrive today that nothing here authors:
 *
 *   `notes`            — a copy of the `ai_notes` COLUMN
 *   `field_confidence` — a copy of the `field_confidence` COLUMN
 *
 * Both are stale twins keyed to the ORIGINAL field ids. Read confidence off the
 * column (see `ExtractionRun.field_confidence`); carry the embedded copy along
 * untouched rather than stripping a key the server chose to keep.
 *
 * `category` is the exception the other way: the model never proposes one, and
 * `approve` requires it. It is written here by the reviewer so a Save Draft
 * survives a reload.
 */
export interface ProposedSchema {
  name?: string;
  category?: string;
  fields?: ProposedField[];
  [key: string]: unknown;
}

/** A run row. snake_case — the raw DB row plus three joined columns. */
export interface ExtractionRun {
  id: string;
  company_id: string;
  source_document_id: string;
  status: string;
  proposed_schema_json: ProposedSchema | null;
  /**
   * `{ field_id: 0..1 }`. The COLUMN, which PATCH never rewrites — so it stays
   * keyed to the ids the model proposed even after a reviewer renames things.
   */
  field_confidence: Record<string, number> | null;
  ai_notes: string | null;
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  /** NUMERIC over the wire, so a STRING: "0.194420". Never a number. */
  cost_usd: string | null;
  error_message: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_template_id: string | null;
  created_at: string;
  updated_at: string;
  original_filename: string | null;
  page_count: number | null;
  /**
   * Present ONLY on a run started for a pre-use template (api#1210); an
   * ordinary run has no such key. See `lib/preuse-extraction.ts`.
   */
  preuse_target?: PreuseTarget;
}

/** A row from `GET /forms/extractions` — the list, not the run. */
export interface ExtractionListRow {
  id: string;
  status: string;
  original_filename: string | null;
  page_count: number | null;
  /** Lifted out of the schema JSON by the handler. Null when Pass 2 proposed none. */
  proposed_name: string | null;
  created_at: string;
}

/** One page image. The `url` is a 3600s lease; see `api.forms.extractionPages`. */
export interface ExtractionPage {
  page: number;
  url: string;
}

// ─── Validation ───────────────────────────────────────────────────────────────

/** A schema complaint, addressed to a dotted path: `fields[3].field_type`. */
export interface SchemaError {
  path: string;
  message: string;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * The API's `validateFormSchema`, mirrored on the client.
 *
 * WHY MIRROR IT AT ALL
 * --------------------
 * Because a proposal can arrive ALREADY INVALID and routinely does. The worker
 * parks a run at `needs_review` even when validation failed — deliberately, so
 * a reviewer sees a mostly-good extraction rather than losing it over one bad
 * field. Both live runs of the same JSA landed with `is_required: true` on a
 * `signature`, which the server refuses at PATCH and again at approve.
 *
 * Without this, a reviewer's first experience is a mystery 422 on a form they
 * never edited. With it, the offending row is marked on first paint.
 *
 * It must stay a MIRROR, not an opinion. Every rule below exists in
 * `form-schema.validator.ts` with the same path and the same wording, so a
 * server 422 lands on the same row this already marked. Rules the client cannot
 * check are simply absent — `maps_to` is STRIPPED server-side rather than
 * rejected, so it is not validated here either.
 */
export function validateProposedSchema(schema: ProposedSchema | null | undefined): SchemaError[] {
  const errors: SchemaError[] = [];

  if (!isPlainObject(schema)) {
    return [{ path: '', message: 'Schema must be an object' }];
  }

  if (typeof schema.name !== 'string' || schema.name.trim() === '') {
    errors.push({ path: 'name', message: 'A form name is required' });
  }

  const fields = schema.fields;
  if (!Array.isArray(fields)) {
    errors.push({ path: 'fields', message: 'fields must be an array' });
    return errors;
  }
  if (fields.length === 0) {
    errors.push({ path: 'fields', message: 'A form must have at least one field' });
  }

  // Collected first so a `show_if` may reference a field defined later, exactly
  // as the server's two-pass check does.
  const allIds = new Set<string>();
  for (const f of fields) {
    if (isPlainObject(f) && typeof f.id === 'string' && f.id !== '') allIds.add(f.id);
  }

  const seenIds = new Set<string>();

  fields.forEach((rawField, i) => {
    const at = (suffix: string) => `fields[${i}]${suffix}`;

    if (!isPlainObject(rawField)) {
      errors.push({ path: at(''), message: 'Field must be an object' });
      return;
    }
    // The guard narrows to Record<string, unknown>, which drops the shape.
    // Every property is re-checked below before use, so the assertion buys
    // readable access without buying trust — same trade as the API's copy.
    const field = rawField as ProposedField;

    if (typeof field.id !== 'string' || field.id.trim() === '') {
      errors.push({ path: at('.id'), message: 'Field id is required' });
    } else if (seenIds.has(field.id)) {
      errors.push({ path: at('.id'), message: `Duplicate field id "${field.id}"` });
    } else {
      seenIds.add(field.id);
    }

    if (!isKnownFieldType(field.field_type)) {
      errors.push({
        path: at('.field_type'),
        message:
          `"${String(field.field_type)}" is not a supported field type. ` +
          `Supported: ${FORM_FIELD_TYPES.join(', ')}`,
      });
    }

    if (typeof field.label !== 'string' || field.label.trim() === '') {
      errors.push({ path: at('.label'), message: 'Field label is required' });
    }

    if (typeof field.field_type === 'string' && CHOICE_TYPES.has(field.field_type)) {
      const choices = field.options?.choices;
      if (!Array.isArray(choices) || choices.length === 0) {
        errors.push({
          path: at('.options.choices'),
          message: `${field.field_type} requires a non-empty options.choices array`,
        });
      } else if (!choices.every((c) => typeof c === 'string' && c.trim() !== '')) {
        errors.push({
          path: at('.options.choices'),
          message: 'Every choice must be a non-empty string',
        });
      }
    }

    if (field.is_required === true && isNonAnswerable(field.field_type)) {
      errors.push({
        path: at('.is_required'),
        message: `A ${field.field_type} field cannot be required — it holds no answer`,
      });
    }

    const showIf = field.conditional_logic?.show_if;
    if (showIf !== undefined && showIf !== null) {
      if (!isPlainObject(showIf)) {
        errors.push({
          path: at('.conditional_logic.show_if'),
          message: 'show_if must be an object of { field_id, equals }',
        });
      } else {
        if (typeof showIf.field_id !== 'string' || !allIds.has(showIf.field_id)) {
          errors.push({
            path: at('.conditional_logic.show_if.field_id'),
            message: `show_if references unknown field id "${String(showIf.field_id)}"`,
          });
        } else if (showIf.field_id === field.id) {
          errors.push({
            path: at('.conditional_logic.show_if.field_id'),
            message: 'A field cannot be conditional on itself',
          });
        }
        if (typeof showIf.equals !== 'string') {
          errors.push({
            path: at('.conditional_logic.show_if.equals'),
            message: 'show_if.equals must be a string',
          });
        }
      }
    }
  });

  return errors;
}

/**
 * Pull `{path, message}` errors out of an axios failure, whichever of the
 * feature's TWO error vocabularies it happens to be speaking.
 *
 * The forms endpoints disagree with each other, and both shapes are live:
 *
 *   PATCH  /forms/extractions/:id           422  path is a DOTTED STRING
 *                                                {"path": "fields[3].field_type", ...}
 *   POST   /forms/extractions/:id/approve   400  path is a ZOD ARRAY
 *                                                {"path": ["category"], "message": "Required"}
 *
 * Normalising here rather than at each call site keeps the disagreement in one
 * place. It is NOT this repo's to reconcile — the fix belongs in the API, and
 * deriving a corrected shape on the client while the server keeps sending the
 * old one is the two-answers-to-one-question failure CLAUDE.md warns about.
 * This only reads what is sent.
 *
 * Returns `[]` when the failure carries no structured errors — a 500, a
 * timeout, a dead-zone. The caller still owes the user `getApiErrorMessage`;
 * an empty array here does NOT mean "nothing was wrong".
 */
export function normalizeSchemaErrors(error: unknown): SchemaError[] {
  const raw = (error as any)?.response?.data?.errors;
  if (!Array.isArray(raw)) return [];

  const out: SchemaError[] = [];
  for (const item of raw) {
    if (!isPlainObject(item)) continue;
    const message = typeof item.message === 'string' ? item.message : '';
    if (!message) continue;

    let path = '';
    if (typeof item.path === 'string') {
      path = item.path;
    } else if (Array.isArray(item.path)) {
      // Zod: ["fields", 3, "field_type"] -> "fields[3].field_type"
      path = item.path.reduce<string>((acc, seg) => {
        if (typeof seg === 'number') return `${acc}[${seg}]`;
        return acc ? `${acc}.${String(seg)}` : String(seg);
      }, '');
    }
    out.push({ path, message });
  }
  return out;
}

/**
 * Group errors by the field index in their path, so a row can render its own.
 *
 * Anything not addressed to a `fields[N]…` path lands under the `form` key —
 * `name`, `fields`, a bare `''` from a malformed document. Those belong at the
 * top of the outline, not on a row, and must not be silently dropped: an error
 * that blocks Approve while being invisible is worse than no error at all.
 */
export function groupErrorsByField(errors: SchemaError[]): {
  byIndex: Map<number, SchemaError[]>;
  form: SchemaError[];
} {
  const byIndex = new Map<number, SchemaError[]>();
  const form: SchemaError[] = [];

  for (const error of errors) {
    const match = /^fields\[(\d+)\]/.exec(error.path);
    if (!match) {
      form.push(error);
      continue;
    }
    const index = Number(match[1]);
    const bucket = byIndex.get(index);
    if (bucket) bucket.push(error);
    else byIndex.set(index, [error]);
  }

  return { byIndex, form };
}

/** The trailing key of a dotted path — `fields[3].options.choices` -> `options.choices`. */
export function fieldErrorKey(path: string): string {
  return path.replace(/^fields\[\d+\]\.?/, '');
}

// ─── Outline ──────────────────────────────────────────────────────────────────

/**
 * A derived section. NOT a thing the payload contains — see the file header.
 *
 * `index` on every entry is the field's position in the FLAT array, and it is
 * the only identity that matters: it is what a `fields[N]` error path addresses
 * and what an edit writes back to. Collapsing a section is a display state and
 * must never touch it.
 */
export interface OutlineSection {
  /** Stable across a rename — the header's field index, or `-1` for the preamble. */
  key: string;
  /** The `section_header` field itself, or null for fields before the first one. */
  header: ProposedField | null;
  headerIndex: number | null;
  title: string;
  /** The fields under this header, EXCLUDING the header. Flat indices preserved. */
  entries: Array<{ field: ProposedField; index: number }>;
}

/**
 * Segment the flat field list on `section_header` rows.
 *
 * ORDER IS THE PAYLOAD'S, NOT OURS. This walks the array once and never sorts:
 * `sort_order` was assigned by the model to match the printed page, and
 * re-deriving an order here would present a different form than the one that
 * gets written to `form_template_fields`.
 *
 * A `section_header` is a REAL FIELD — it is inserted as a row like any other,
 * and it renders as a heading in the filled form. It is shown as the section's
 * title AND remains editable in place; collapsing hides its children, never the
 * header, and never removes it from the document.
 *
 * Fields appearing before the first header (every JSA starts with a handful:
 * company, date, job name) go into a leading section with a null header, so
 * nothing is orphaned off the top of the outline.
 */
export function buildOutline(fields: ProposedField[]): OutlineSection[] {
  const sections: OutlineSection[] = [];
  let current: OutlineSection | null = null;

  fields.forEach((field, index) => {
    if (field.field_type === 'section_header') {
      current = {
        key: `section-${index}`,
        header: field,
        headerIndex: index,
        title: (field.label ?? '').trim() || 'Untitled section',
        entries: [],
      };
      sections.push(current);
      return;
    }

    if (!current) {
      current = {
        key: 'section-preamble',
        header: null,
        headerIndex: null,
        title: 'Form details',
        entries: [],
      };
      sections.push(current);
    }
    current.entries.push({ field, index });
  });

  return sections;
}

// ─── Confidence ───────────────────────────────────────────────────────────────

/**
 * Is this field one the model was unsure about?
 *
 * ABSENT IS NOT LOW. A field with no entry in the map — a field the reviewer
 * ADDED, or one the model scored nothing for — returns false and wears no
 * amber. Treating a missing score as 0 would paint every hand-added field as
 * suspect, which is the render-absent-as-zero failure in a different costume.
 */
export function isLowConfidence(
  confidence: Record<string, number> | null | undefined,
  fieldId: string | undefined,
): boolean {
  if (!confidence || !fieldId) return false;
  const score = confidence[fieldId];
  if (!Number.isFinite(score)) return false;
  return score < LOW_CONFIDENCE_THRESHOLD;
}

/** The score as a percentage string, or `—` when there is none. Never `0%`. */
export function formatConfidence(
  confidence: Record<string, number> | null | undefined,
  fieldId: string | undefined,
): string {
  if (!confidence || !fieldId) return '—';
  const score = confidence[fieldId];
  if (!Number.isFinite(score)) return '—';
  return `${Math.round(score * 100)}%`;
}

// ─── Naming ───────────────────────────────────────────────────────────────────

/**
 * Does this name already belong to a live form in the same category?
 *
 * Only ENABLED, ACTIVE templates count. A draft is not in front of anyone and a
 * disabled one is not offered, so neither is a name a crew could confuse this
 * with — and warning about them would train reviewers to ignore the warning.
 *
 * Case- and whitespace-insensitive: "Job Safety Analysis (JSA)" and
 * "job safety analysis (jsa) " are the same form to a person holding a tablet,
 * and that is whose problem this is.
 */
export function findDuplicateTemplate(
  templates: FormLibraryTemplate[],
  name: string,
  category: string,
): FormLibraryTemplate | null {
  const needle = name.trim().toLowerCase();
  if (!needle || !category) return null;
  return (
    templates.find(
      (t) =>
        t.category === category &&
        t.status === 'active' &&
        t.isEnabled &&
        (t.name ?? '').trim().toLowerCase() === needle,
    ) ?? null
  );
}
